import React, { useState, useEffect } from 'react';
import { 
  Dialog, DialogTitle, DialogContent, DialogActions, 
  Button, Box, Typography, IconButton, 
  LinearProgress, Stepper, Step, StepButton, Paper, Divider 
} from '@mui/material';
import MedicalInformationIcon from '@mui/icons-material/MedicalInformation';
import CloseIcon from '@mui/icons-material/Close';
import axios from 'axios';
import { toast } from 'react-toastify';

import { steps, ColorConnector, CustomStepIcon } from './AddPatientStyles';
import StepIdentity from './steps/StepIdentity';
import StepLifePath from './steps/StepLifePath';
import StepMedical from './steps/StepMedical';
import StepDocumentsPortal from './steps/StepDocumentsPortal';

const AddPatientDialog = ({ open, onClose, onPatientAdded, apiUrl, token }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  
  const [searchQuery, setSearchQuery] = useState('');
  const [clientOptions, setClientOptions] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const [formData, setFormData] = useState({
    nom_patient: '',
    prenom_patient: '',
    email_patient: '',
    telephone_patient: '',
    lieu_residence_patient: '',
    date_naissance_patient: '',
    profession_patient: '',
    statut_pro_patient: 'actif',
    situation_familiale_patient: '',
    enfants_age_patient: '',
    personne_confiance_lien_prenom: '',
    personne_confiance_tel: '',
    connaissance_soa: '',
    premiere_visite_date: '',
    coordination_principale: '',
    date_entretien: '',
    pratique_ou_coordination: '',
    coordinatrices: '',
    enfance_famille_naissance: '',
    vie_couple_famille: '',
    vie_professionnelle: '',
    vie_aujourd_hui: '',
    resume_chemin_de_vie: '',
    parcours_general: '',
    origine_probable_decrite: '',
    deroule_cancer: '',
    pathologie_patient: '',
    corps_medical: '',
    etat_sante_aujourd_hui: '',
    effets_indesirables_physique: '',
    effets_indesirables_psy_emo: '',
    analyse_medicale: '',
    traitement_allopathique: '',
    traitement_alternatif: '',
    alimentation: '',
    allergies: '',
    activites_physiques: '',
    sommeil: '',
    loisirs: '',
    attentes_soa: '',
    situation_financiere: 'ras',
    notes_financieres: '',
    autre_info: '',
    password_client: '',
    addresse_client: '',
    ville_client: '',
    cp_client: '',
    phone_client: '',
    isExistingClient: false,
  });

  const [photoFile, setPhotoFile] = useState(null);
  const [documentFiles, setDocumentFiles] = useState([]);
// Ajoute cet effet dans ton composant AddPatientDialog.jsx
useEffect(() => {
  if (open) {
    // Réinitialise à l'étape 0 et vide le formulaire à l'ouverture
    setActiveStep(0);
    setFormData({
      nom_patient: '',
      prenom_patient: '',
      email_patient: '',
      telephone_patient: '',
      lieu_residence_patient: '',
      date_naissance_patient: '',
      profession_patient: '',
      statut_pro_patient: 'actif',
      situation_familiale_patient: '',
      enfants_age_patient: '',
      personne_confiance_lien_prenom: '',
      personne_confiance_tel: '',
      connaissance_soa: '',
      premiere_visite_date: '',
      coordination_principale: '',
      date_entretien: '',
      pratique_ou_coordination: '',
      coordinatrices: '',
      enfance_famille_naissance: '',
      vie_couple_famille: '',
      vie_professionnelle: '',
      vie_aujourd_hui: '',
      resume_chemin_de_vie: '',
      parcours_general: '',
      origine_probable_decrite: '',
      deroule_cancer: '',
      pathologie_patient: '',
      corps_medical: '',
      etat_sante_aujourd_hui: '',
      effets_indesirables_physique: '',
      effets_indesirables_psy_emo: '',
      analyse_medicale: '',
      traitement_allopathique: '',
      traitement_alternatif: '',
      alimentation: '',
      allergies: '',
      activites_physiques: '',
      sommeil: '',
      loisirs: '',
      attentes_soa: '',
      situation_financiere: 'ras',
      notes_financieres: '',
      autre_info: '',
      password_client: '',
      addresse_client: '',
      ville_client: '',
      cp_client: '',
      phone_client: '',
      client_id: '',
      isExistingClient: false,
    });
    setPhotoFile(null);
    setDocumentFiles([]);
    setErrors({});
    setSearchQuery('');
  }
}, [open]);
  useEffect(() => {
    if (!open || !apiUrl || !token) return;

    const fetchClients = async () => {
      setSearchLoading(true);
      try {
        const response = await axios.get(`${apiUrl}/admin/clients/check-email/all`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { query: searchQuery } 
        });

        const results = response.data.data || response.data.clients || response.data || [];
        setClientOptions(Array.isArray(results) ? results : [results]);
      } catch (err) {
        console.error("Erreur lors de la recherche des clients", err);
      } finally {
        setSearchLoading(false);
      }
    };

    const timer = setTimeout(() => {
      fetchClients();
    }, 300);

    return () => clearTimeout(timer);
  }, [open, apiUrl, token, searchQuery]);

  // Mets à jour la fonction de sélection du client existant :
const handleSelectExistingClient = (event, selectedClient) => {
  if (!selectedClient) {
    setFormData(prev => ({
      ...prev,
      client_id: '',
      isExistingClient: false,
    }));
    return;
  }

  setFormData(prev => ({
    ...prev,
    client_id: selectedClient.id_client || selectedClient.id || '',
    isExistingClient: true,
    nom_patient: selectedClient.nom_client || selectedClient.nom || selectedClient.nom_patient || '',
    prenom_patient: selectedClient.prenom_client || selectedClient.prenom || selectedClient.prenom_patient || '',
    email_patient: selectedClient.email_client || selectedClient.email || selectedClient.email_patient || '',
    telephone_patient: selectedClient.phone_client || selectedClient.phone || selectedClient.telephone_client || prev.telephone_patient,
    addresse_client: selectedClient.addresse_client || prev.addresse_client,
    ville_client: selectedClient.ville_client || prev.ville_client,
    cp_client: selectedClient.cp_client || prev.cp_client,
    phone_client: selectedClient.phone_client || selectedClient.phone || prev.phone_client,
  }));

  toast.info(`Client sélectionné : Informations pré-remplies.`);
};

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleAddDocuments = (e) => {
    const newFiles = Array.from(e.target.files || []);
    if (newFiles.length === 0) return;
    setDocumentFiles((prev) => [...prev, ...newFiles]);
    e.target.value = '';
  };

  const handleRemoveDocument = (index) => {
    setDocumentFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const validateStep = (step) => {
    if (step === 0) {
      const newErrors = {};
      if (!formData.nom_patient.trim()) {
        newErrors.nom_patient = 'Le nom est requis';
      }
      if (!formData.prenom_patient.trim()) {
        newErrors.prenom_patient = 'Le prénom est requis';
      }
      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    }
    return true;
  };

  const handleNext = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (loading) return;
    if (!validateStep(activeStep)) return;
    setActiveStep((prev) => Math.min(prev + 1, steps.length - 1));
  };

  const handleBack = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (loading) return;
    setActiveStep((prev) => Math.max(prev - 1, 0));
  };

  const handleStepClick = (stepIndex) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (loading) return;
    if (stepIndex <= activeStep) {
      setActiveStep(stepIndex);
      return;
    }
    if (validateStep(activeStep)) {
      setActiveStep(stepIndex);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (activeStep !== steps.length - 1) return;
    if (loading) return;
    if (!validateStep(0)) {
      setActiveStep(0);
      toast.error('Merci de compléter les champs obligatoires');
      return;
    }
    setLoading(true);
    try {
      const data = new FormData();
      Object.keys(formData).forEach((key) => {
        const value = formData[key];
        if (value !== null && value !== undefined && value !== '') {
          data.append(key, value);
        }
      });
      if (photoFile) {
        data.append('photo_patient', photoFile);
      }
      if (documentFiles.length > 0) {
        documentFiles.forEach((file) => {
          data.append('documents[]', file);
        });
      }
      const response = await axios.post(`${apiUrl}/admin/patients`, data, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });
      if (response.data?.success) {
        toast.success('Dossier patient et compte client enregistrés avec succès !');
        if (onPatientAdded) {
          onPatientAdded();
        }
        onClose();
      } else {
        toast.error(response.data?.message || "Erreur lors de l'enregistrement du dossier");
      }
    } catch (err) {
      console.error('Erreur création patient :', err);
      if (err.response?.status === 422) {
        const validationErrors = err.response?.data?.errors || {};
        setErrors(validationErrors);
        const firstError = Object.values(validationErrors)?.[0]?.[0];
        toast.error(firstError || 'Veuillez vérifier les informations saisies.');
      } else {
        toast.error(err.response?.data?.message || "Erreur lors de l'enregistrement du dossier");
      }
    } finally {
      setLoading(false);
    }
  };

  const progressPercent = Math.round(((activeStep + 1) / steps.length) * 100);

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <StepIdentity 
            formData={formData} 
            handleChange={handleChange} 
            errors={errors} 
            clientOptions={clientOptions} 
            searchLoading={searchLoading} 
            setSearchQuery={setSearchQuery} 
            handleSelectExistingClient={handleSelectExistingClient} 
            photoFile={photoFile} 
            setPhotoFile={setPhotoFile} 
          />
        );
      case 1:
        return <StepLifePath formData={formData} handleChange={handleChange} />;
      case 2:
        return <StepMedical formData={formData} handleChange={handleChange} />;
      case 3:
        return (
          <StepDocumentsPortal 
            formData={formData} 
            handleChange={handleChange} 
            documentFiles={documentFiles} 
            handleAddDocuments={handleAddDocuments} 
            handleRemoveDocument={handleRemoveDocument} 
          />
        );
      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth PaperProps={{ sx: { borderRadius: '16px', minHeight: '85vh' } }}>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#F4F7FE', px: 4, py: 2 }}>
        <Box display="flex" alignItems="center" gap={1.5}>
          <MedicalInformationIcon sx={{ color: '#4318FF', fontSize: 28 }} />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800, color: '#2B3674', lineHeight: 1.2 }}>Enregistrement du dossier patient</Typography>
            <Typography variant="caption" sx={{ color: '#A3AED0' }}>Étape {activeStep + 1} sur {steps.length} {' — '} {steps[activeStep].subtitle}</Typography>
          </Box>
        </Box>
        <IconButton type="button" onClick={onClose} size="small" sx={{ color: '#A3AED0', '&:hover': { color: '#2B3674' } }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <LinearProgress variant="determinate" value={progressPercent} sx={{ height: 3, backgroundColor: '#E0E5F2', '& .MuiLinearProgress-bar': { backgroundColor: '#4318FF' } }} />
      <Box sx={{ px: 4, pt: 3, pb: 1, backgroundColor: '#F8F9FA' }}>
        <Stepper activeStep={activeStep} alternativeLabel connector={<ColorConnector />}>
          {steps.map((step, index) => (
            <Step key={step.label}>
              <StepButton type="button" onClick={handleStepClick(index)}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: .5 }}>
                  <CustomStepIcon active={activeStep === index} completed={activeStep > index} icon={index + 1} />
                  <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: activeStep === index ? '#2B3674' : '#A3AED0' }}>{step.label}</Typography>
                </Box>
              </StepButton>
            </Step>
          ))}
        </Stepper>
      </Box>
      <form onSubmit={handleSubmit} noValidate>
        <DialogContent sx={{ px: 4, py: 2.5, backgroundColor: '#F8F9FA', minHeight: '450px' }}>
          <Paper elevation={0} sx={{ p: 3.5, borderRadius: '12px', border: '1px solid #E0E5F2', backgroundColor: '#FFFFFF' }}>
            {renderStepContent(activeStep)}
          </Paper>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 4, py: 2, backgroundColor: '#F4F7FE', justifyContent: 'space-between' }}>
          <Button type="button" disabled={activeStep === 0 || loading} onClick={handleBack} sx={{ fontWeight: 600, color: activeStep === 0 ? '#CBD5E1' : '#4318FF' }}>Retour</Button>
          <Box>
            <Button type="button" onClick={onClose} disabled={loading} color="inherit" sx={{ mr: 2, fontWeight: 600, color: '#A3AED0' }}>Annuler</Button>
            {activeStep < steps.length - 1 ? (
              <Button type="button" variant="contained" onClick={handleNext} disabled={loading} sx={{ backgroundColor: '#4318FF', fontWeight: 700, borderRadius: '8px', px: 4, '&:hover': { backgroundColor: '#3311CC' } }}>Suivant</Button>
            ) : (
              <Button type="submit" variant="contained" disabled={loading} sx={{ backgroundColor: '#05CD99', fontWeight: 700, borderRadius: '8px', px: 4, '&:hover': { backgroundColor: '#04B384' } }}>{loading ? 'Enregistrement...' : 'Enregistrer le dossier complet'}</Button>
            )}
          </Box>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default AddPatientDialog;